import { State, Toast } from '@seiue/ui'
import { Button, Upload, UploadFile, UploadProps } from 'antd'
import { RcFile } from 'antd/lib/upload'
import { useAtomValue } from 'jotai'
import { FC, useEffect, useState } from 'react'
import styled from 'styled-components'

import { GrowpIcon } from 'packages/components/GrowpIcon'
import { FileDirName } from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'
import { formatMsTimeToString, uploadFile } from 'packages/utils'

import { WaringText } from '@/components/Styled'
import { getTheme } from '@/utils/theme'

import { Tips, FileDisplay, DisplayMsgLabel, UploadButton } from './Styled'
import { UploadTaskFileProps } from './UploadTaskFile'
import { validFileExistAtom } from './atom'

/**
 * 上传视频组件
 *
 * @param param0 - 参数
 * @param param0.setFile - 设置表单文件
 * @param param0.file - 表单文件
 * @returns 上传视频组件
 */
export const UploadVideo: FC<UploadTaskFileProps> = ({
  setFile: setFormFile,
  file: formFile,
}) => {
  const [file, setFile] = useState<
    (UploadFile & { duration?: string }) | undefined
  >(formFile)

  const [loading, setLoading] = useState<boolean>(false)

  const validFileExist = useAtomValue(validFileExistAtom)

  const theme = getTheme()

  useEffect(() => {
    if (!file) {
      setFile(formFile)
    }
  }, [formFile])

  const beforeUpload: UploadProps['beforeUpload'] = async (
    file: RcFile & { url?: string }
  ) => {
    const fileSizeM = file.size / 1024 / 1024
    if (fileSizeM > 300) {
      Toast.warning(`请上传体积小于300M的视频文件`)

      file.url = undefined
      return false
    }

    const handleReaderLoad = (event: AnyType) => {
      const arrayBuffer = event.target.result
      const blob = new Blob([arrayBuffer])
      const url = URL.createObjectURL(blob)
      // use the video URL to load video metadata
      const video = document.createElement('video')
      video.src = url
      video.addEventListener('loadedmetadata', async () => {
        const duration = formatMsTimeToString(video.duration)
        // 如果视频时长大于两小时，
        if (video.duration > 7200000) {
          Toast.warning(`视频时长不能超过两小时`)
          file.url = undefined
          return
        }
        setLoading(true)
        const { data } = await uploadFile({
          file,
          dirname: FileDirName.Video,
        })
        setLoading(false)
        if (!data) return

        file.url = data?.url
        // 使用 ... 拓展运算符之后，数据就变成了 json 格式，里面的数据计算属性值不能解构出来
        setFormFile({
          name: file?.name,
          size: file?.size,
          type: file?.type,
          uid: file?.uid,
          url: data.url,
          duration,
        })
        setFile({
          name: file?.name,
          size: file?.size,
          type: file?.type,
          uid: file?.uid,
          url: data.url,
          duration,
        })
      })
    }

    const reader = new FileReader()
    reader.onload = handleReaderLoad
    reader.readAsArrayBuffer(file)

    return file
  }

  return (
    <div>
      {file ? (
        <FileDisplay>
          <DisplayMsgLabel>{`视频名称：${file.name}`}</DisplayMsgLabel>
          <DisplayMsgLabel>{`视频时长：${
            file.duration || '-'
          }`}</DisplayMsgLabel>
          <OperateBtns>
            <Upload
              accept=".mp4,.flv,.avi,.mov"
              beforeUpload={beforeUpload}
              fileList={[]}
            >
              <Button
                type="link"
                style={{
                  marginRight: 0,
                  padding: 0,
                  color: theme.brand._1,
                  border: 'none',
                }}
              >
                {'更换'}
              </Button>
            </Upload>
            <Button
              type="link"
              onClick={() => {
                setFile(undefined)
                setFormFile(undefined)
              }}
              style={{ color: theme.status.danger.element }}
            >
              {'删除'}
            </Button>
          </OperateBtns>
        </FileDisplay>
      ) : loading ? (
        <State.Loading
          style={{ minHeight: 16, position: 'absolute', top: 0, left: 8 }}
        />
      ) : (
        <div>
          <Upload
            accept=".mp4,.flv,.avi,.mov"
            beforeUpload={beforeUpload}
            fileList={[]}
          >
            <UploadButton
              $backgroundColor={
                !file && validFileExist
                  ? theme.status.danger.backgroundLight
                  : undefined
              }
            >
              <GrowpIcon name="Jiahao" />
              <div>上传视频</div>
            </UploadButton>
          </Upload>
          <Tips>{'视频大小上限300M，支持格式mp4、flv、avi、mov'}</Tips>
          {!file && validFileExist ? (
            <WaringText>{'请上传视频'}</WaringText>
          ) : null}
        </div>
      )}
    </div>
  )
}

const OperateBtns = styled.div`
  display: flex;
`
